import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { api } from '../../utils/api';
import { toast } from 'sonner';
import { AlertTriangle, Code } from 'lucide-react';

const TrackingEditor = () => {
  const [loading, setLoading] = useState(false);
  const agent = JSON.parse(localStorage.getItem('agent') || '{}');
  const [tracking, setTracking] = useState({
    google_ads_code: '',
    meta_pixel_code: '',
    other_scripts: '',
    enabled: false
  });

  useEffect(() => {
    // Load existing tracking codes
    const loadTracking = async () => {
      try {
        const data = await api.getAgentPublic(agent.slug);
        if (data.tracking && Object.keys(data.tracking).length > 0) {
          setTracking(prev => ({ ...prev, ...data.tracking }));
        }
      } catch (error) {
        console.error('Failed to load tracking codes:', error);
      }
    };
    if (agent.slug) loadTracking();
  }, [agent.slug]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.updateTracking(agent.slug, tracking);
      toast.success('Codes de tracking mis à jour', {
        description: tracking.enabled ? 'Les codes sont actifs sur votre page.' : 'Les codes sont désactivés.'
      });
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setTracking(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-3xl font-bold mb-2">Codes de tracking</h1>
        <p className="text-muted-foreground">
          Ajoutez vos pixels de conversion et scripts de tracking
        </p>
      </div>

      {/* Enable/Disable Toggle */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold">Activer le tracking</h3>
            <p className="text-sm text-muted-foreground">
              Les codes seront injectés uniquement sur votre page (/a/{agent.slug})
            </p>
          </div>
          <Switch
            checked={tracking.enabled}
            onCheckedChange={(checked) => updateField('enabled', checked)}
            data-testid="tracking-enabled-switch"
          />
        </div>
      </Card>

      {/* Warning */}
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          <strong>Attention :</strong> Collez uniquement des scripts provenant de sources fiables.
          Un code malveillant peut compromettre votre page.
        </AlertDescription>
      </Alert>

      {/* Tracking Codes */}
      <Tabs defaultValue="google" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="google">
            <Code className="w-4 h-4 mr-2" />
            Google Ads
          </TabsTrigger>
          <TabsTrigger value="meta">
            <Code className="w-4 h-4 mr-2" />
            Meta Pixel
          </TabsTrigger>
          <TabsTrigger value="other">
            <Code className="w-4 h-4 mr-2" />
            Autres
          </TabsTrigger>
        </TabsList>

        <TabsContent value="google">
          <Card className="p-6 space-y-4">
            <div>
              <Label htmlFor="google_ads_code">Code Google Ads / Tag Manager</Label>
              <Textarea
                id="google_ads_code"
                value={tracking.google_ads_code}
                onChange={(e) => updateField('google_ads_code', e.target.value)}
                rows={8}
                className="font-mono text-sm"
                placeholder={`<!-- Google tag (gtag.js) -->
<script async src="https://www.googletagmanager.com/gtag/js?id=AW-XXXXXXXXX"></script>
<script>
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', 'AW-XXXXXXXXX');
</script>`}
                data-testid="google-ads-code-input"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Collez le code complet fourni par Google Ads ou Tag Manager (inclut les balises &lt;script&gt;)
              </p>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="meta">
          <Card className="p-6 space-y-4">
            <div>
              <Label htmlFor="meta_pixel_code">Code Meta Pixel (Facebook)</Label>
              <Textarea
                id="meta_pixel_code"
                value={tracking.meta_pixel_code}
                onChange={(e) => updateField('meta_pixel_code', e.target.value)}
                rows={8}
                className="font-mono text-sm"
                placeholder={`<!-- Meta Pixel Code -->
<script>
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', 'YOUR_PIXEL_ID');
fbq('track', 'PageView');
</script>`}
                data-testid="meta-pixel-code-input"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Collez le code complet fourni par Meta Business (inclut les balises &lt;script&gt;)
              </p>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="other">
          <Card className="p-6 space-y-4">
            <div>
              <Label htmlFor="other_scripts">Autres scripts</Label>
              <Textarea
                id="other_scripts"
                value={tracking.other_scripts}
                onChange={(e) => updateField('other_scripts', e.target.value)}
                rows={8}
                className="font-mono text-sm"
                placeholder="<!-- Autres scripts de tracking -->"
                data-testid="other-scripts-input"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Pour tout autre pixel ou script (Hotjar, Matomo, etc.)
              </p>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex gap-4">
        <Button onClick={handleSave} disabled={loading} data-testid="save-tracking-button">
          {loading ? 'Enregistrement...' : 'Enregistrer les codes de tracking'}
        </Button>
        {tracking.enabled && (
          <Alert className="flex-1">
            <AlertDescription className="text-sm">
              ✅ Les codes de tracking sont <strong>actifs</strong> sur votre page
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
};

export default TrackingEditor;
